import React, { useState, useEffect } from 'react';
import { Linkedin, Users, ExternalLink } from 'lucide-react';
import { team } from '../../mock';
import SEO from '../SEO';

function TeamCard({ member }) {
  const [imgError, setImgError] = useState(false);

  return (
    <article className="group relative rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-[#d42b92]/60 transition-all duration-300 flex flex-col overflow-hidden shadow-xl hover:shadow-[0_12px_36px_-10px_rgba(212,43,146,0.25)] hover:-translate-y-1.5">
      {/* Subtle ambient hover glow */}
      <div className="absolute -top-16 -right-16 w-36 h-36 rounded-full bg-[#d42b92]/0 group-hover:bg-[#d42b92]/20 blur-3xl transition-all duration-500 pointer-events-none" />

      {/* Photo Header */}
      <div className="relative w-full aspect-[4/3.8] overflow-hidden bg-white/[0.03] shrink-0">
        {!imgError && member.image ? (
          <img
            src={member.image}
            alt={member.name}
            onError={() => setImgError(true)}
            className="w-full h-full object-cover object-top filter grayscale contrast-105 group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#d42b92]/20 via-black to-black text-[#ff8ec5] font-bold text-3xl">
            {member.fallbackInitials || member.initials}
          </div>
        )}

        {/* Soft bottom vignette gradient to blend photo with card body */}
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#0e0e12] via-[#0e0e12]/40 to-transparent pointer-events-none" />

        {/* Category Pill Tag */}
        <div className="absolute top-3 left-3 z-10">
          <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-wider uppercase backdrop-blur-md bg-black/75 border border-white/15 text-white/80 group-hover:border-[#d42b92]/50 group-hover:text-white transition-colors">
            {member.category}
          </span>
        </div>

        {/* LinkedIn Quick Action */}
        {member.linkedin && (
          <a
            href={member.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${member.name} on LinkedIn`}
            className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/75 backdrop-blur-md border border-white/15 text-white/70 hover:text-white hover:bg-[#0077b5] hover:border-[#0077b5] flex items-center justify-center transition-all shadow-md"
          >
            <Linkedin className="w-3.5 h-3.5 fill-current" />
          </a>
        )}
      </div>

      {/* Card Body */}
      <div className="p-5 sm:p-6 flex-1 flex flex-col justify-between">
        <div>
          {/* Member Name */}
          <h3 className="text-lg sm:text-xl font-bold text-white tracking-tight group-hover:text-[#ff8ec5] transition-colors leading-snug">
            {member.name}
          </h3>

          {/* Member Designation */}
          <div className="mt-1.5 text-xs sm:text-[13px] font-semibold text-[#ff8ec5] tracking-wide leading-relaxed min-h-[38px] flex items-center">
            {member.designation || (member.category === 'Core Team' ? 'Venture Team' : 'Strategic Advisor')}
          </div>

          {/* Member Intro / Bio */}
          <p className="mt-3 text-xs sm:text-[13px] text-white/70 leading-relaxed font-normal line-clamp-3">
            {member.intro || "Active contributor to InnovHer's venture-building ecosystem, startup acceleration, and ecosystem growth."}
          </p>
        </div>

        {/* Card Footer */}
        <div className="mt-5 pt-3.5 border-t border-white/10 flex items-center justify-end">
          {member.linkedin ? (
            <a
              href={member.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/60 hover:text-[#ff8ec5] transition-colors group/link"
            >
              <span>Connect</span>
              <ExternalLink className="w-3 h-3 group-hover/link:translate-x-0.5 transition-transform" />
            </a>
          ) : (
            <span className="text-[11px] text-white/30 italic">InnovHer Core</span>
          )}
        </div>
      </div>
    </article>
  );
}

export default function TeamPage() {
  const categories = ['All', 'Leadership', 'Core Team', 'Strategic Partner'];
  const [activeTab, setActiveTab] = useState('All');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const els = document.querySelectorAll('.reveal-up');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-revealed');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const leadershipMembers = team.filter((m) => m.category === 'Leadership');
  const coreTeamMembers = team.filter((m) => m.category === 'Core Team');
  const strategicPartners = team.filter((m) => m.category === 'Strategic Partner');

  const showLeadership = activeTab === 'All' || activeTab === 'Leadership';
  const showCoreTeam = activeTab === 'All' || activeTab === 'Core Team';
  const showStrategicPartners = activeTab === 'All' || activeTab === 'Strategic Partner';

  return (
    <div className="min-h-screen bg-[#08080a] text-white">
      <SEO
        title="The Team | InnovHer"
        description="Experience that moves ventures forward. Meet the team driving InnovHer."
        keywords="InnovHer team, Raman Poonia, Harshit Soni, Aditya Malik, Dr. Shweta Choudhary, Jeet Vijay, Jaipur"
      />

      {/* Hero / Header Section */}
      <section className="relative pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-20 border-b border-white/5 overflow-hidden">
        {/* Ambient glow */}
        <div className="absolute top-1/3 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#d42b92]/10 blur-[180px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="max-w-4xl reveal-up">
            <div className="text-[#ff8ec5] text-xs font-semibold tracking-[0.3em] uppercase mb-4 flex items-center gap-2">
              {/* <span className="w-2 h-2 rounded-full bg-[#d42b92] inline-block animate-pulse" /> */}
              <span>The Team</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.08]">
              Experience that moves <span className="text-[#d42b92]">ventures forward.</span>
            </h1>

            <div className="mt-8 space-y-4 text-white/80 text-base sm:text-lg leading-relaxed font-normal">
              <p>
                At the heart of InnovHer is a growing network of experts who have built, led, invested, created and learned across industries.
              </p>
              <p>
                The core team works alongside founders through the everyday realities of building, supported by mentors, advisors, investors, domain experts and business leaders who bring specialized experience when it matters.
              </p>
              <p>
                Rather than creating a large passive network, InnovHer is designed around meaningful access, connecting ventures to relevant expertise, perspectives and relationships at different stages of their journey.
              </p>
              <p className="text-white/95 font-medium">
                Because the strongest ecosystems are not defined by how many people they know, but by how effectively the right people come together to create progress.
              </p>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="mt-14 flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-5 reveal-up">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-[#d42b92]/15 border border-[#d42b92]/30 flex items-center justify-center">
                <Users className="w-4 h-4 text-[#ff8ec5]" />
              </div>
              <span className="text-sm font-bold text-white tracking-wide">
                Directory ({team.length})
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
              {categories.map((cat) => {
                const count = cat === 'All' ? team.length : team.filter((m) => m.category === cat).length;
                const active = activeTab === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setActiveTab(cat)}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider transition-all flex items-center gap-2 ${
                      active
                        ? 'bg-[#d42b92] text-white shadow-lg shadow-[#d42b92]/30 scale-[1.02]'
                        : 'border border-white/10 bg-white/[0.02] text-white/60 hover:text-white hover:border-white/20'
                    }`}
                  >
                    <span>{cat}</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                        active ? 'bg-white/20 text-white' : 'bg-white/5 text-white/40'
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Three Clearly Separated Sections */}
          <div className="mt-12 lg:mt-16 space-y-16 lg:space-y-20">
            {/* Section 1: Leadership */}
            {showLeadership && (
              <section aria-labelledby="heading-leadership" className="space-y-8">
                <div className="flex items-center gap-4">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                  <h2
                    id="heading-leadership"
                    className="text-sm sm:text-base font-bold tracking-[0.2em] uppercase text-white/70 text-center flex items-center gap-2"
                  >
                    <span className="w-2 h-2 rounded-full bg-[#d42b92] inline-block" />
                    Leadership
                    <span className="text-xs text-white/40 font-normal">({leadershipMembers.length})</span>
                  </h2>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-7">
                  {leadershipMembers.map((member) => (
                    <TeamCard key={member.id || member.name} member={member} />
                  ))}
                </div>
              </section>
            )}

            {/* Section 2: Core Team */}
            {showCoreTeam && (
              <section aria-labelledby="heading-core-team" className="space-y-8">
                <div className="flex items-center gap-4">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                  <h2
                    id="heading-core-team"
                    className="text-sm sm:text-base font-bold tracking-[0.2em] uppercase text-white/70 text-center flex items-center gap-2"
                  >
                    <span className="w-2 h-2 rounded-full bg-[#d42b92] inline-block" />
                    Core Team
                    <span className="text-xs text-white/40 font-normal">({coreTeamMembers.length})</span>
                  </h2>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                </div>

                {/* Balanced 2-card centered layout */}
                <div className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-7">
                  {coreTeamMembers.map((member) => (
                    <TeamCard key={member.id || member.name} member={member} />
                  ))}
                </div>
              </section>
            )}

            {/* Section 3: Strategic Partners */}
            {showStrategicPartners && (
              <section aria-labelledby="heading-strategic-partners" className="space-y-8">
                <div className="flex items-center gap-4">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                  <h2
                    id="heading-strategic-partners"
                    className="text-sm sm:text-base font-bold tracking-[0.2em] uppercase text-white/70 text-center flex items-center gap-2"
                  >
                    <span className="w-2 h-2 rounded-full bg-[#d42b92] inline-block" />
                    Strategic Partners
                    <span className="text-xs text-white/40 font-normal">({strategicPartners.length})</span>
                  </h2>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-7">
                  {strategicPartners.map((member) => (
                    <TeamCard key={member.id || member.name} member={member} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
